import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import SenderDashboard from "@/pages/SenderDashboard";
import OwnerDashboard from "@/pages/OwnerDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import LoadDetail from "@/pages/LoadDetail";
import Documents from "@/pages/Documents";
import AuthCallback from "@/pages/AuthCallback";
import TrackPage from "@/pages/TrackPage";
import OpsDashboard from "@/pages/OpsDashboard";
import AccountsDashboard from "@/pages/AccountsDashboard";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import TripDetail from "@/pages/TripDetail";
import FleetPage from "@/pages/FleetPage";
import DriversPage from "@/pages/DriversPage";
import CustomersPage from "@/pages/CustomersPage";
import PartiesPage from "@/pages/PartiesPage";
import RecycleBinPage from "@/pages/RecycleBinPage";
import { PrintLR, PrintBill, PrintDriverCard } from "@/pages/Prints";

const DASH = {
  sender: "/sender",
  lorry_owner: "/owner",
  driver: "/driver",
  admin: "/admin",
  super_admin: "/superadmin",
  accounts: "/accounts",
  loading_supervisor: "/ops",
};

function Protected({ children, role }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#0A0A0A] text-white p-10">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role) {
    const roles = Array.isArray(role) ? role : [role];
    if (!roles.includes(user.role) && user.role !== "admin" && user.role !== "super_admin") {
      return <Navigate to={DASH[user.role] || "/"} replace />;
    }
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Public tracking */}
      <Route path="/track" element={<TrackPage />} />
      <Route path="/track/:cn" element={<TrackPage />} />

      {/* Marketplace side */}
      <Route path="/sender" element={<Protected role="sender"><SenderDashboard /></Protected>} />
      <Route path="/owner" element={<Protected role="lorry_owner"><OwnerDashboard /></Protected>} />
      <Route path="/driver" element={<Protected role="driver"><DriverDashboard /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/documents" element={<Protected role={["lorry_owner", "driver"]}><Documents /></Protected>} />
      <Route path="/loads/:id" element={<Protected><LoadDetail /></Protected>} />

      {/* Ops side */}
      <Route path="/ops" element={<Protected role={["loading_supervisor", "super_admin", "admin", "accounts"]}><OpsDashboard /></Protected>} />
      <Route path="/accounts" element={<Protected role={["accounts", "super_admin", "admin"]}><AccountsDashboard /></Protected>} />
      <Route path="/superadmin" element={<Protected role={["super_admin", "admin"]}><SuperAdminDashboard /></Protected>} />
      <Route path="/fleet" element={<Protected role={["loading_supervisor", "accounts", "super_admin", "admin"]}><FleetPage /></Protected>} />
      <Route path="/drivers-db" element={<Protected role={["loading_supervisor", "accounts", "super_admin", "admin"]}><DriversPage /></Protected>} />
      <Route path="/customers" element={<Protected role={["loading_supervisor", "accounts", "super_admin", "admin"]}><CustomersPage /></Protected>} />
      <Route path="/parties" element={<Protected role={["loading_supervisor", "accounts", "super_admin", "admin"]}><PartiesPage /></Protected>} />
      <Route path="/recycle-bin" element={<Protected role={["super_admin", "admin"]}><RecycleBinPage /></Protected>} />
      <Route path="/trips/:id" element={<Protected><TripDetail /></Protected>} />

      {/* Printables */}
      <Route path="/prints/lr/:id" element={<Protected><PrintLR /></Protected>} />
      <Route path="/prints/bill/:id" element={<Protected><PrintBill /></Protected>} />
      <Route path="/prints/driver-card/:id" element={<Protected><PrintDriverCard /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
